import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Check, X, AlertCircle, RefreshCw, Search, Download, Trash2, RotateCcw, AlertTriangle, Building2, Image, Filter } from "lucide-react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useDispatch } from "react-redux";
import { getFilters } from "../../store/filters";

const API_BASE = '/api';

const PAGE_SIZE = 50;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const statusColors = {
  active: { dot: '#10b981', bg: 'transparent', text: '#166534', pillBg: '#dcfce7' },
  inactive: { dot: '#ef4444', bg: '#FEF2F2', text: '#991b1b', pillBg: '#fee2e2' },
  follower: { dot: '#eab308', bg: '#FFFDE7', text: '#854d0e', pillBg: '#fef9c3' },
  hidden: { dot: '#6b7280', bg: '#F3F4F6', text: '#374151', pillBg: '#e5e7eb' },
};

const getRowBackground = (status) => {
  const s = (status || 'active').toLowerCase();
  if (s === 'inactive') return '#FEF2F2';
  if (s === 'follower') return '#FFFDE7';
  if (s === 'hidden') return '#F3F4F6';
  return 'transparent';
};

const styles = {
  container: {
    padding: '24px',
    paddingTop: '100px',
    maxWidth: '100%',
    margin: '0 auto',
    fontFamily: "'Poppins', sans-serif",
    height: '100vh',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#1a1a1a',
  },
  searchRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    marginBottom: '16px',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: '400px',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 40px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    outline: 'none',
    transition: 'all 0.2s',
  },
  searchInputFocus: {
    border: '1px solid #DEC2AA',
    boxShadow: '0 0 0 3px rgba(222, 194, 170, 0.2)',
  },
  resultCount: {
    fontSize: '13px',
    color: '#6b7280',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
    borderBottom: '2px solid #e5e7eb',
  },
  tab: {
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#1a1a1a',
    borderBottom: '2px solid #DEC2AA',
    background: 'linear-gradient(to bottom, rgba(222, 194, 170, 0.1), transparent)',
  },
  tableWrapper: {
    overflow: 'auto',
    maxHeight: 'calc(100vh - 360px)',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    background: '#fff',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    position: 'sticky',
    top: 0,
    background: '#f9fafb',
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    whiteSpace: 'nowrap',
    zIndex: 10,
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#1f2937',
    maxWidth: '250px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  tdEditing: {
    padding: '4px 8px',
    background: '#fffbeb',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '2px solid #DEC2AA',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    outline: 'none',
    background: '#fff',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  cellActions: {
    display: 'flex',
    gap: '4px',
    marginTop: '4px',
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  saveBtn: {
    background: '#10b981',
    color: '#fff',
  },
  cancelBtn: {
    background: '#ef4444',
    color: '#fff',
  },
  error: {
    fontSize: '11px',
    color: '#ef4444',
    marginTop: '2px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    color: '#6b7280',
  },
  rowNumber: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: 500,
    textAlign: 'center',
    width: '50px',
    background: '#f9fafb',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#1a1a1a',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#DEC2AA',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
  },
  saving: {
    background: '#fef3c7',
    color: '#92400e',
  },
  saved: {
    background: '#dcfce7',
    color: '#166534',
  },
  errorBadge: {
    background: '#fee2e2',
    color: '#dc2626',
  },
  arrayCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  arrayTag: {
    display: 'inline-block',
    padding: '2px 8px',
    background: '#f3f4f6',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#4b5563',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '200px',
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 100,
    marginTop: '4px',
  },
  dropdownOption: {
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background 0.15s',
  },
  dropdownOptionHover: {
    background: '#f9fafb',
  },
  dropdownOptionSelected: {
    background: 'rgba(222, 194, 170, 0.2)',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    background: '#DEC2AA',
    borderColor: '#DEC2AA',
  },
  selectedTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '8px',
  },
  selectedTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: '#DEC2AA',
    color: '#1a1a1a',
    borderRadius: '4px',
    fontSize: '11px',
  },
  removeTag: {
    cursor: 'pointer',
    opacity: 0.7,
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '2px solid #DEC2AA',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#fff',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '440px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    fontFamily: "'Poppins', sans-serif",
    border: '2px solid #DEC2AA',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  modalText: {
    fontSize: '14px',
    color: '#4b5563',
    marginBottom: '24px',
    lineHeight: 1.6,
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  modalCancelBtn: {
    padding: '10px 20px',
    background: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
  },
  modalDeleteBtn: {
    padding: '10px 20px',
    background: '#1a1a1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
  },
  modalRestoreBtn: {
    padding: '10px 20px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
  },
  modalPermanentDeleteBtn: {
    padding: '10px 20px',
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
  },
  deleteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    background: 'transparent',
    color: '#9ca3af',
    transition: 'all 0.2s',
  },
  restoreBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    background: '#dcfce7',
    color: '#166534',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  permDeleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    background: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  investorStatusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    userSelect: 'none',
    border: 'none',
    background: 'none',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  filterPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#6b7280',
    transition: 'all 0.2s',
    fontFamily: "'Poppins', sans-serif",
  },
  filterPillActive: {
    background: '#1a1a1a',
    color: '#fff',
    borderColor: '#1a1a1a',
  },
  filterCount: {
    fontSize: '11px',
    padding: '1px 6px',
    borderRadius: '9999px',
    background: 'rgba(0,0,0,0.1)',
  },
  filterCountActive: {
    background: 'rgba(255,255,255,0.2)',
  },
  filterLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#9ca3af',
    fontWeight: 500,
    marginRight: '4px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e5e7eb',
  },
  avatarPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ca3af',
    border: '2px solid #e5e7eb',
  },
  firmPickerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  firmPickerContent: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '480px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    fontFamily: "'Poppins', sans-serif",
    border: '2px solid #DEC2AA',
  },
  firmPickerSearch: {
    width: '100%',
    padding: '10px 12px 10px 40px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
  },
  firmPickerList: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: '400px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  firmPickerItem: {
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #f3f4f6',
    transition: 'background 0.15s',
  },
  firmPickerItemSelected: {
    background: 'rgba(222, 194, 170, 0.25)',
    fontWeight: 600,
  },
  firmPickerActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
};

const stageOptions = [
  'Seed', 'Pre-Seed', 'Series A', 'Series B', 'Series C', 'Series D', 
  'Early Stage', 'Growth', 'Pre-IPO', 'Public', 'Buyout', 'Mid market', 'Late Stage', 'Secondary'
];

const sectorOptions = [
  'Agritech', 'Automobile', 'B2B', 'Chemicals', 'Consumer', 'Edtech', 
  'Fintech', 'Healthcare', 'Metal', 'Real Estate', 'SaaS', 
  'Sector Agnostic', 'Sustainability', 'Technology', 'Enterprise', 'Logistics'
];

const typeOptions = ['Investor', 'Banker', 'Debt', 'Others'];

const subCategoryOptions = ['Advisors', 'Legal', 'Others'];

const levelOptions = ['L1', 'L2', 'L3'];

const investorColumns = [
  { key: 'photo', label: 'Photo', type: 'photo' },
  { key: 'name', label: 'Name', type: 'string', required: true },
  { key: 'firmId', label: 'Firm', type: 'firm' },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'level', label: 'Level', type: 'select', options: levelOptions },
  { key: 'designation', label: 'Designation', type: 'string' },
  { key: 'linkedIn', label: 'LinkedIn', type: 'url' },
  { key: 'type', label: 'Type', type: 'select', options: typeOptions },
  { key: 'subCategory', label: 'Sub-Category', type: 'select', options: subCategoryOptions },
  { key: 'stage', label: 'Stages', type: 'multiselect', options: stageOptions },
  { key: 'sector', label: 'Sectors', type: 'multiselect', options: sectorOptions },
  { key: 'locationCities', label: 'Cities', type: 'array' },
  { key: 'locationCountries', label: 'Countries', type: 'array' },
  { key: 'pastInvestments', label: 'Past Investments', type: 'array' },
  { key: 'remarks', label: 'Remarks', type: 'string' },
];

const firmColumns = [
  { key: 'firmName', label: 'Firm Name', type: 'string', required: true },
  { key: 'website', label: 'Website', type: 'url' },
];

const deletedColumns = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'level', label: 'Level' },
  { key: 'type', label: 'Type' },
  { key: 'deletedAt', label: 'Deleted At' },
];

const validateField = (value, type, required) => {
  if (required && (!value || (Array.isArray(value) && value.length === 0))) {
    return 'This field is required';
  }
  
  if (!value || value === '') return null;
  
  switch (type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return 'Invalid email format';
      break;
    case 'url':
      if (value && !value.match(/^https?:\/\/.+/i) && value.length > 0) {
        return 'URL must start with http:// or https://';
      }
      break;
    default:
      break;
  }
  
  return null;
};

const EditableCell = ({ value, column, onSave, rowId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  
  const startEditing = () => {
    if (column.type === 'multiselect') {
      setSelectedItems(Array.isArray(value) ? [...value] : []);
    } else if (column.type === 'array') {
      setEditValue(Array.isArray(value) ? value.join(', ') : '');
    } else {
      setEditValue(value || '');
    }
    setError(null);
    setIsEditing(true);
  };
  
  const cancelEditing = () => {
    setIsEditing(false);
    setError(null);
    setSelectedItems([]);
  };
  
  const saveValue = async () => {
    let finalValue = editValue;
    
    if (column.type === 'multiselect') {
      finalValue = selectedItems;
    } else if (column.type === 'array') {
      finalValue = editValue.split(',').map(s => s.trim()).filter(s => s);
    }
    
    const validationError = validateField(finalValue, column.type, column.required);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(rowId, column.key, finalValue);
      setIsEditing(false);
      setError(null);
      setSelectedItems([]);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      cancelEditing();
    } else if (e.key === 'Enter' && !e.shiftKey && column.type !== 'multiselect') {
      e.preventDefault();
      saveValue();
    }
  };
  
  const toggleOption = (option) => {
    setSelectedItems(prev => 
      prev.includes(option) 
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };
  
  if (isEditing) {
    if (column.type === 'select') {
      return (
        <td style={{ ...styles.td, ...styles.tdEditing }}>
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            style={styles.select}
            disabled={isSaving}
          >
            <option value="">Select...</option>
            {column.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <div style={styles.cellActions}>
            <button onClick={saveValue} style={{ ...styles.iconBtn, ...styles.saveBtn }} disabled={isSaving} title="Save">
              <Check size={14} />
            </button>
            <button onClick={cancelEditing} style={{ ...styles.iconBtn, ...styles.cancelBtn }} disabled={isSaving} title="Cancel">
              <X size={14} />
            </button>
          </div>
          {error && <div style={styles.error}><AlertCircle size={12} />{error}</div>}
        </td>
      );
    }
    
    if (column.type === 'multiselect') {
      return (
        <td style={{ ...styles.td, ...styles.tdEditing, minWidth: '200px' }}>
          {selectedItems.length > 0 && (
            <div style={styles.selectedTags}>
              {selectedItems.map(item => (
                <span key={item} style={styles.selectedTag}>
                  {item}
                  <span style={styles.removeTag} onClick={() => toggleOption(item)}>
                    <X size={10} />
                  </span>
                </span>
              ))}
            </div>
          )}
          <div style={styles.dropdownList}>
            {column.options?.map(opt => (
              <div
                key={opt}
                style={{
                  ...styles.dropdownOption,
                  ...(hoveredOption === opt ? styles.dropdownOptionHover : {}),
                  ...(selectedItems.includes(opt) ? styles.dropdownOptionSelected : {})
                }}
                onClick={() => toggleOption(opt)}
                onMouseEnter={() => setHoveredOption(opt)}
                onMouseLeave={() => setHoveredOption(null)}
              >
                <div style={{
                  ...styles.checkbox,
                  ...(selectedItems.includes(opt) ? styles.checkboxChecked : {})
                }}>
                  {selectedItems.includes(opt) && <Check size={10} color="#fff" />}
                </div>
                {opt}
              </div>
            ))}
          </div>
          <div style={{ ...styles.cellActions, marginTop: '8px' }}>
            <button onClick={saveValue} style={{ ...styles.iconBtn, ...styles.saveBtn }} disabled={isSaving} title="Save">
              <Check size={14} />
            </button>
            <button onClick={cancelEditing} style={{ ...styles.iconBtn, ...styles.cancelBtn }} disabled={isSaving} title="Cancel">
              <X size={14} />
            </button>
          </div>
          {error && <div style={styles.error}><AlertCircle size={12} />{error}</div>}
        </td>
      );
    }
    
    return (
      <td style={{ ...styles.td, ...styles.tdEditing }}>
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
          placeholder={column.type === 'array' ? 'Comma separated values' : ''}
          disabled={isSaving}
        />
        <div style={styles.cellActions}>
          <button onClick={saveValue} style={{ ...styles.iconBtn, ...styles.saveBtn }} disabled={isSaving} title="Save (Enter)">
            <Check size={14} />
          </button>
          <button onClick={cancelEditing} style={{ ...styles.iconBtn, ...styles.cancelBtn }} disabled={isSaving} title="Cancel (Esc)">
            <X size={14} />
          </button>
        </div>
        {error && <div style={styles.error}><AlertCircle size={12} />{error}</div>}
      </td>
    );
  }
  
  const displayValue = () => {
    if ((column.type === 'array' || column.type === 'multiselect') && Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: '#9ca3af' }}>-</span>;
      return (
        <div style={styles.arrayCell}>
          {value.slice(0, 3).map((item, i) => (
            <span key={i} style={styles.arrayTag}>{item}</span>
          ))}
          {value.length > 3 && (
            <span style={styles.arrayTag}>+{value.length - 3} more</span>
          )}
        </div>
      );
    }
    return value || <span style={{ color: '#9ca3af' }}>-</span>;
  };
  
  return (
    <td 
      style={styles.td}
      onDoubleClick={startEditing}
      title="Double-click to edit"
    >
      {displayValue()}
    </td>
  );
};

const PhotoCell = ({ value, onSave, rowId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEditing = () => {
    setEditValue(value || '');
    setError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setError(null);
  };

  const saveValue = async () => {
    setIsSaving(true);
    try {
      await onSave(rowId, 'photo', editValue);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') cancelEditing();
    else if (e.key === 'Enter') { e.preventDefault(); saveValue(); }
  };

  if (isEditing) {
    return (
      <td style={{ ...styles.td, ...styles.tdEditing, minWidth: '180px' }}>
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
          placeholder="Paste image URL..."
          disabled={isSaving}
        />
        <div style={styles.cellActions}>
          <button onClick={saveValue} style={{ ...styles.iconBtn, ...styles.saveBtn }} disabled={isSaving} title="Save">
            <Check size={14} />
          </button>
          <button onClick={cancelEditing} style={{ ...styles.iconBtn, ...styles.cancelBtn }} disabled={isSaving} title="Cancel">
            <X size={14} />
          </button>
        </div>
        {error && <div style={styles.error}><AlertCircle size={12} />{error}</div>}
      </td>
    );
  }

  return (
    <td style={{ ...styles.td, width: '50px', textAlign: 'center' }} onDoubleClick={startEditing} title="Double-click to edit photo URL">
      {value ? (
        <img src={value} alt="" style={styles.avatar} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
      ) : null}
      <div style={{ ...styles.avatarPlaceholder, display: value ? 'none' : 'flex' }}>
        <Image size={14} />
      </div>
    </td>
  );
};

const FirmPickerModal = ({ isOpen, onClose, firms, currentFirmId, onSelect }) => {
  const [search, setSearch] = useState('');

  if (!isOpen) return null;

  const filtered = firms.filter(f =>
    (f.firmName || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.firmPickerOverlay} onClick={onClose}>
      <div style={styles.firmPickerContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalTitle}>
          <Building2 size={20} color="#DEC2AA" />
          Select Firm
        </div>
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search firms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.firmPickerSearch}
            autoFocus
          />
        </div>
        <div style={styles.firmPickerList}>
          {filtered.length > 0 ? filtered.map(firm => (
            <div
              key={firm._id}
              style={{
                ...styles.firmPickerItem,
                ...(firm._id === currentFirmId ? styles.firmPickerItemSelected : {}),
              }}
              onClick={() => onSelect(firm._id)}
              onMouseEnter={e => { if (firm._id !== currentFirmId) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { if (firm._id !== currentFirmId) e.currentTarget.style.background = ''; }}
            >
              <span>{firm.firmName}</span>
              {firm._id === currentFirmId && <Check size={16} color="#DEC2AA" />}
            </div>
          )) : (
            <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
              No firms found
            </div>
          )}
        </div>
        <div style={styles.firmPickerActions}>
          <button
            style={styles.modalCancelBtn}
            onClick={() => onSelect(null)}
          >
            Clear Firm
          </button>
          <button style={styles.modalCancelBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel, confirmStyle }) => {
  if (!isOpen) return null;
  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalTitle}>
          <AlertTriangle size={20} color="#DEC2AA" />
          {title}
        </div>
        <div style={styles.modalText}>{message}</div>
        <div style={styles.modalActions}>
          <button style={styles.modalCancelBtn} onClick={onCancel}>Cancel</button>
          <button style={confirmStyle || styles.modalDeleteBtn} onClick={onConfirm}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
};

const StatusFilterRow = ({ statusFilter, setStatusFilter, counts, includeDeleted, includeHidden }) => {
  let filters = ['all', 'active', 'inactive', 'follower'];
  if (includeHidden) filters.push('hidden');
  if (includeDeleted) filters.push('deleted');

  const labels = { all: 'All', active: 'Active', inactive: 'Inactive', follower: 'Follower', deleted: 'Deleted', hidden: 'Hidden' };

  return (
    <div style={styles.filterRow}>
      <span style={styles.filterLabel}>
        <Filter size={13} />
        Status:
      </span>
      {filters.map(f => (
        <button
          key={f}
          style={{
            ...styles.filterPill,
            ...(statusFilter === f ? styles.filterPillActive : {}),
          }}
          onClick={() => setStatusFilter(f)}
        >
          {labels[f]}
          <span style={{
            ...styles.filterCount,
            ...(statusFilter === f ? styles.filterCountActive : {}),
          }}>
            {counts[f] || 0}
          </span>
        </button>
      ))}
    </div>
  );
};

const FILTER_CATEGORIES = [
  "Type",
  "Funding Round",
  "Sector",
  "Country",
  "City",
];

const FiltersManager = () => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("Type");
  const [newValue, setNewValue] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const loadFilters = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/filters`, { headers: getAuthHeaders() });
      setFilters(res.data?.filters || []);
    } catch (e) {
      setError("Failed to load filters");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const current = filters.find((f) => f.category === activeCategory) || { category: activeCategory, values: [] };
  const visibleValues = (current.values || []).filter((v) =>
    v.toLowerCase().includes(search.toLowerCase())
  );

  const updateLocal = (category, values) => {
    setFilters((prev) => {
      const exists = prev.some((f) => f.category === category);
      if (exists) return prev.map((f) => (f.category === category ? { ...f, values } : f));
      return [...prev, { category, values }];
    });
  };

  const handleAdd = async () => {
    const value = newValue.trim();
    if (!value || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE}/filters/value`,
        { category: activeCategory, value },
        { headers: getAuthHeaders() }
      );
      updateLocal(res.data.category, res.data.values);
      dispatch(getFilters());
      setNewValue("");
    } catch (e) {
      setError(e.response?.data?.error || "Failed to add value");
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (oldValue) => {
    const value = editValue.trim();
    if (!value || busy) return;
    if (value === oldValue) { setEditing(null); return; }
    setBusy(true);
    setError("");
    try {
      const res = await axios.put(
        `${API_BASE}/filters/value`,
        { category: activeCategory, oldValue, newValue: value },
        { headers: getAuthHeaders() }
      );
      updateLocal(res.data.category, res.data.values);
      dispatch(getFilters());
      setEditing(null);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to rename value");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (value) => {
    if (busy) return;
    if (!window.confirm(`Remove "${value}" from ${activeCategory}?`)) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.delete(`${API_BASE}/filters/value`, {
        headers: getAuthHeaders(),
        data: { category: activeCategory, value },
      });
      updateLocal(res.data.category, res.data.values);
      dispatch(getFilters());
    } catch (e) {
      setError(e.response?.data?.error || "Failed to remove value");
    } finally {
      setBusy(false);
    }
  };

  const categoryList = [
    ...FILTER_CATEGORIES,
    ...filters.map((f) => f.category).filter((c) => !FILTER_CATEGORIES.includes(c)),
  ];

  const fm = {
    wrap: { display: 'flex', gap: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff', overflow: 'hidden', maxHeight: 'calc(100vh - 300px)' },
    sidebar: { width: '220px', borderRight: '1px solid #e5e7eb', background: '#fafafa', overflowY: 'auto', flexShrink: 0 },
    catBtn: (active) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px 16px', border: 'none', borderLeft: active ? '3px solid #DEC2AA' : '3px solid transparent', background: active ? 'rgba(222,194,170,0.15)' : 'transparent', color: '#1a1a1a', fontSize: '13px', fontWeight: active ? 600 : 500, cursor: 'pointer', fontFamily: "'Poppins', sans-serif", textAlign: 'left' }),
    catCount: { fontSize: '11px', color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1px 8px' },
    main: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
    addRow: { display: 'flex', gap: '8px' },
    input: { flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: "'Poppins', sans-serif" },
    addBtn: { padding: '9px 16px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: "'Poppins', sans-serif" },
    valuesWrap: { display: 'flex', flexDirection: 'column', gap: '6px' },
    valueRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: '8px', background: '#fff' },
    iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px', display: 'inline-flex' },
  };

  return (
    <div style={fm.wrap}>
      <div style={fm.sidebar}>
        {categoryList.map((cat) => {
          const c = filters.find((f) => f.category === cat);
          return (
            <button
              key={cat}
              style={fm.catBtn(activeCategory === cat)}
              onClick={() => { setActiveCategory(cat); setEditing(null); setSearch(''); setError(''); }}
            >
              <span>{cat}</span>
              <span style={fm.catCount}>{c?.values?.length || 0}</span>
            </button>
          );
        })}
      </div>
      <div style={fm.main}>
        <div style={fm.addRow}>
          <input
            style={fm.input}
            placeholder={`Add a value to ${activeCategory}...`}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          />
          <button style={fm.addBtn} onClick={handleAdd} disabled={busy}>Add</button>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ ...fm.input, paddingLeft: '34px', width: '100%', boxSizing: 'border-box' }}
            placeholder="Search values..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: '13px' }}>{error}</div>}
        {loading ? (
          <div style={{ color: '#6b7280', fontSize: '13px', padding: '12px' }}>
            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px', verticalAlign: 'middle' }} />
            Loading filters...
          </div>
        ) : (
          <div style={fm.valuesWrap}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{visibleValues.length} value{visibleValues.length === 1 ? '' : 's'}</div>
            {visibleValues.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: '13px', padding: '24px', textAlign: 'center' }}>No values yet</div>
            ) : (
              visibleValues.map((value) => (
                <div key={value} style={fm.valueRow}>
                  {editing === value ? (
                    <>
                      <input
                        style={fm.input}
                        value={editValue}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(value); if (e.key === 'Escape') setEditing(null); }}
                      />
                      <button style={fm.iconBtn} onClick={() => handleRename(value)} title="Save"><Check size={16} color="#16a34a" /></button>
                      <button style={fm.iconBtn} onClick={() => setEditing(null)} title="Cancel"><X size={16} /></button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '13px', color: '#1a1a1a' }}>{value}</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={fm.iconBtn} onClick={() => { setEditing(value); setEditValue(value); }} title="Rename"><RefreshCw size={14} /></button>
                        <button style={fm.iconBtn} onClick={() => handleDelete(value)} title="Delete"><Trash2 size={14} color="#dc2626" /></button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DatabaseEditor = () => {
  
  const [activeTab, setActiveTab] = useState('investors');
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, investor: null });
  const [permanentDeleteModal, setPermanentDeleteModal] = useState({ open: false, investor: null });
  const [restoreModal, setRestoreModal] = useState({ open: false, investor: null });
  const [allInvestorsData, setAllInvestorsData] = useState([]);
  const [firmsData, setFirmsData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [investorStatusFilter, setInvestorStatusFilter] = useState('all');
  const [firmStatusFilter, setFirmStatusFilter] = useState('all');
  const [firmPickerModal, setFirmPickerModal] = useState({ open: false, investorId: null, currentFirmId: null });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const investorsData = allInvestorsData;

  const deletedInvestors = useMemo(() => {
    return allInvestorsData.filter(inv => (inv.status || 'active').toLowerCase() === 'deleted');
  }, [allInvestorsData]);

  const firmsMap = useMemo(() => {
    const map = {};
    firmsData.forEach(f => { map[f._id] = f; });
    return map;
  }, [firmsData]);

  const investorStatusCounts = useMemo(() => {
    const counts = { all: allInvestorsData.length, active: 0, inactive: 0, follower: 0, deleted: 0 };
    allInvestorsData.forEach(inv => {
      const s = (inv.status || 'active').toLowerCase();
      if (counts[s] !== undefined) counts[s]++;
      else counts.active++;
    });
    return counts;
  }, [allInvestorsData]);

  const firmStatusCounts = useMemo(() => {
    const counts = { all: firmsData.length, active: 0, inactive: 0, follower: 0, hidden: 0 };
    firmsData.forEach(f => {
      const s = (f.status || 'active').toLowerCase();
      if (counts[s] !== undefined) counts[s]++;
      else counts.active++;
    });
    return counts;
  }, [firmsData]);

  const fetchAllData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [invRes, firmRes] = await Promise.all([
        axios.get(`${API_BASE}/investors?includeDeleted=true`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/investment-firms`, { headers: getAuthHeaders() }),
      ]);
      setAllInvestorsData(invRes.data.investors || invRes.data || []);
      setFirmsData(firmRes.data.firms || firmRes.data || []);
    } catch (err) {
      setGlobalError('Failed to load data from database');
      console.error('DB fetch error:', err);
    } finally {
      setDataLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  const handleSaveInvestor = useCallback(async (id, field, value) => {
    setSaveStatus('saving');
    setGlobalError(null);
    
    try {
      const investor = allInvestorsData.find(inv => inv._id === id);
      if (!investor) throw new Error('Investor not found');
      
      const updatedData = { ...investor, [field]: value };
      
      await axios.put(
        `${API_BASE}/investors/${id}`,
        updatedData,
        { headers: getAuthHeaders() }
      );
      
      await fetchAllData();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      setGlobalError(err.message || 'Failed to save');
      setTimeout(() => {
        setSaveStatus(null);
        setGlobalError(null);
      }, 5000);
      throw err;
    }
  }, [allInvestorsData, fetchAllData]);

  const handleSoftDelete = useCallback(async (investor) => {
    setSaveStatus('saving');
    setGlobalError(null);
    try {
      const updatedData = { ...investor, status: 'deleted', deletedAt: new Date().toISOString() };
      await axios.put(
        `${API_BASE}/investors/${investor._id}`,
        updatedData,
        { headers: getAuthHeaders() }
      );
      await fetchAllData();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      setGlobalError(err.message || 'Failed to delete');
      setTimeout(() => { setSaveStatus(null); setGlobalError(null); }, 5000);
    }
  }, [fetchAllData]);

  const handleRestore = useCallback(async (investor) => {
    setSaveStatus('saving');
    setGlobalError(null);
    try {
      const updatedData = { ...investor, status: 'active', deletedAt: null };
      await axios.put(
        `${API_BASE}/investors/${investor._id}`,
        updatedData,
        { headers: getAuthHeaders() }
      );
      await fetchAllData();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      setGlobalError(err.message || 'Failed to restore');
      setTimeout(() => { setSaveStatus(null); setGlobalError(null); }, 5000);
    }
  }, [fetchAllData]);

  const handlePermanentDelete = useCallback(async (investor) => {
    setSaveStatus('saving');
    setGlobalError(null);
    try {
      await axios.delete(
        `${API_BASE}/investors/${investor._id}`,
        { headers: getAuthHeaders() }
      );
      await fetchAllData();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      setGlobalError(err.message || 'Failed to permanently delete');
      setTimeout(() => { setSaveStatus(null); setGlobalError(null); }, 5000);
    }
  }, [fetchAllData]);

  
  const handleSaveFirm = useCallback(async (id, field, value) => {
    setSaveStatus('saving');
    setGlobalError(null);
    
    try {
      const firm = firmsData.find(f => f._id === id);
      if (!firm) throw new Error('Firm not found');
      
      const updatedData = { ...firm, [field]: value };
      
      await axios.put(
        `${API_BASE}/investment-firms/${id}`,
        updatedData,
        { headers: getAuthHeaders() }
      );
      
      await fetchAllData();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      setGlobalError(err.message || 'Failed to save');
      setTimeout(() => {
        setSaveStatus(null);
        setGlobalError(null);
      }, 5000);
      throw err;
    }
  }, [firmsData, fetchAllData]);
  
  const handleRefresh = async () => {
    setIsLoading(true);
    setGlobalError(null);
    try {
      await fetchAllData();
    } catch (err) {
      setGlobalError('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  };

  const statusFilteredInvestors = useMemo(() => {
    if (investorStatusFilter === 'all') return investorsData;
    return investorsData.filter(inv => (inv.status || 'active').toLowerCase() === investorStatusFilter);
  }, [investorsData, investorStatusFilter]);

  const statusFilteredFirms = useMemo(() => {
    if (firmStatusFilter === 'all') return firmsData;
    return firmsData.filter(f => (f.status || 'active').toLowerCase() === firmStatusFilter);
  }, [firmsData, firmStatusFilter]);
  
  const rawData = activeTab === 'investors' ? statusFilteredInvestors : activeTab === 'firms' ? statusFilteredFirms : deletedInvestors;
  const columns = activeTab === 'investors' ? investorColumns : activeTab === 'firms' ? firmColumns : deletedColumns;
  const onSave = activeTab === 'investors' ? handleSaveInvestor : handleSaveFirm;
  const loading = dataLoading;
  
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return rawData;
    
    const query = searchQuery.toLowerCase().trim();
    return rawData.filter(row => {
      return columns.some(col => {
        if (col.type === 'photo' || col.type === 'firm') return false;
        const value = row[col.key];
        if (!value) return false;
        
        if (Array.isArray(value)) {
          return value.some(item => 
            String(item).toLowerCase().includes(query)
          );
        }
        return String(value).toLowerCase().includes(query);
      });
    });
  }, [rawData, searchQuery, columns]);

  const displayedData = useMemo(
    () => filteredData.slice(0, visibleCount),
    [filteredData, visibleCount]
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab, searchQuery, investorStatusFilter, firmStatusFilter]);

  const handleTableScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        setVisibleCount((prev) =>
          prev < filteredData.length ? prev + PAGE_SIZE : prev
        );
      }
    },
    [filteredData.length]
  );
  
  const handleExport = () => {
    const exportColumns = activeTab === 'investors' ? investorColumns : activeTab === 'firms' ? firmColumns : deletedColumns;
    const dataToExport = filteredData.map(row => {
      const exportRow = {};
      exportColumns.forEach(col => {
        if (col.type === 'photo') {
          exportRow[col.label] = row[col.key] || '';
          return;
        }
        if (col.type === 'firm') {
          const firm = firmsMap[row.firmId];
          exportRow[col.label] = firm ? firm.firmName : '';
          return;
        }
        const value = row[col.key];
        if (Array.isArray(value)) {
          exportRow[col.label] = value.join(', ');
        } else {
          exportRow[col.label] = value || '';
        }
      });
      return exportRow;
    });
    
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    const sheetName = activeTab === 'investors' ? 'Investors' : activeTab === 'firms' ? 'Firms' : 'Deleted';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `${sheetName}_${date}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const handleStatusChange = useCallback(async (investor, newStatus) => {
    try {
      await handleSaveInvestor(investor._id, 'status', newStatus);
    } catch (err) {}
  }, [handleSaveInvestor]);

  const handleFirmStatusChange = useCallback(async (firm, newStatus) => {
    try {
      await handleSaveFirm(firm._id, 'status', newStatus);
    } catch (err) {}
  }, [handleSaveFirm]);

  const handleFirmSelect = useCallback(async (firmId) => {
    const investorId = firmPickerModal.investorId;
    setFirmPickerModal({ open: false, investorId: null, currentFirmId: null });
    if (investorId) {
      try {
        await handleSaveInvestor(investorId, 'firmId', firmId || '');
      } catch (err) {}
    }
  }, [firmPickerModal.investorId, handleSaveInvestor]);

  const renderStatusCell = (investor) => {
    const status = (investor.status || 'active').toLowerCase();
    const colors = statusColors[status] || statusColors.active;
    return (
      <td style={{ ...styles.td, padding: '4px 8px' }}>
        <select
          value={status}
          onChange={(e) => handleStatusChange(investor, e.target.value)}
          style={{
            padding: '4px 8px',
            borderRadius: '9999px',
            border: 'none',
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: "'Poppins', sans-serif",
            background: colors.pillBg,
            color: colors.text,
            cursor: 'pointer',
            outline: 'none',
            appearance: 'auto',
          }}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="follower">Follower</option>
          <option value="deleted">Deleted</option>
        </select>
      </td>
    );
  };

  const renderFirmStatusCell = (firm) => {
    const status = (firm.status || 'active').toLowerCase();
    const colors = statusColors[status] || statusColors.active;
    return (
      <td style={{ ...styles.td, padding: '4px 8px' }}>
        <select
          value={status}
          onChange={(e) => handleFirmStatusChange(firm, e.target.value)}
          style={{
            padding: '4px 8px',
            borderRadius: '9999px',
            border: 'none',
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: "'Poppins', sans-serif",
            background: colors.pillBg,
            color: colors.text,
            cursor: 'pointer',
            outline: 'none',
            appearance: 'auto',
          }}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="follower">Follower</option>
          <option value="hidden">Hidden</option>
        </select>
      </td>
    );
  };

  const renderDeleteCell = (investor) => {
    return (
      <td style={{ ...styles.td, width: '40px', textAlign: 'center' }}>
        <button
          style={styles.deleteBtn}
          onClick={() => setDeleteModal({ open: true, investor })}
          title="Delete investor"
          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
        >
          <Trash2 size={14} />
        </button>
      </td>
    );
  };

  const renderDeletedActions = (investor) => {
    return (
      <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            style={styles.restoreBtn}
            onClick={() => setRestoreModal({ open: true, investor })}
            title="Restore investor"
          >
            <RotateCcw size={12} /> Restore
          </button>
          <button
            style={styles.permDeleteBtn}
            onClick={() => setPermanentDeleteModal({ open: true, investor })}
            title="Permanently delete"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </td>
    );
  };

  const renderInvestorCell = (row, col) => {
    if (col.type === 'photo') {
      return (
        <PhotoCell
          key={col.key}
          value={row[col.key]}
          rowId={row._id}
          onSave={handleSaveInvestor}
        />
      );
    }
    if (col.type === 'firm') {
      const firm = firmsMap[row.firmId];
      return (
        <td
          key={col.key}
          style={{ ...styles.td, cursor: 'pointer' }}
          onDoubleClick={() => setFirmPickerModal({ open: true, investorId: row._id, currentFirmId: row.firmId || null })}
          title="Double-click to change firm"
        >
          {firm ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Building2 size={12} color="#6b7280" />
              {firm.firmName}
            </span>
          ) : (
            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No Firm</span>
          )}
        </td>
      );
    }
    return (
      <EditableCell
        key={col.key}
        value={row[col.key]}
        column={col}
        rowId={row._id}
        onSave={onSave}
      />
    );
  };
  
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Database Editor</h1>
        <div style={styles.headerActions}>
          {saveStatus && (
            <span style={{ 
              ...styles.statusBadge, 
              ...(saveStatus === 'saving' ? styles.saving : saveStatus === 'error' ? styles.errorBadge : styles.saved) 
            }}>
              {saveStatus === 'saving' ? (
                <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
              ) : saveStatus === 'error' ? (
                <><AlertCircle size={12} /> Error</>
              ) : (
                <><Check size={12} /> Saved</>
              )}
            </span>
          )}
          {globalError && (
            <span style={{ ...styles.statusBadge, ...styles.errorBadge }}>
              {globalError}
            </span>
          )}
          <button 
            onClick={handleExport} 
            style={styles.exportBtn}
            disabled={filteredData.length === 0}
          >
            <Download size={14} />
            Export
          </button>
          <button 
            onClick={handleRefresh} 
            style={styles.refreshBtn}
            disabled={isLoading}
          >
            <RefreshCw size={14} style={isLoading ? { animation: 'spin 1s linear infinite' } : {}} />
            Refresh
          </button>
        </div>
      </div>
      
      {activeTab !== 'filters' && (
      <div style={styles.searchRow}>
        <div style={styles.searchWrapper}>
          <Search size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              ...styles.searchInput,
              ...(searchFocused ? styles.searchInputFocus : {})
            }}
          />
        </div>
        {searchQuery ? (
          <span style={styles.resultCount}>
            {filteredData.length} of {rawData.length} results
          </span>
        ) : (
          <span style={styles.resultCount}>
            Showing {Math.min(visibleCount, filteredData.length)} of {filteredData.length}
          </span>
        )}
      </div>
      )}
      
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'investors' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('investors'); setSearchQuery(''); }}
        >
          Investors ({investorsData.length || 0})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'firms' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('firms'); setSearchQuery(''); }}
        >
          Firms ({firmsData.length || 0})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'deleted' ? styles.tabActive : {}), ...(deletedInvestors.length > 0 ? { color: activeTab === 'deleted' ? '#1a1a1a' : '#dc2626' } : {}) }}
          onClick={() => { setActiveTab('deleted'); setSearchQuery(''); }}
        >
          Deleted ({deletedInvestors.length || 0})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'filters' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('filters'); setSearchQuery(''); }}
        >
          <Filter size={14} style={{ verticalAlign: 'middle', marginRight: '6px', marginTop: '-2px' }} />
          Filters
        </button>
      </div>

      {activeTab === 'investors' && (
        <StatusFilterRow
          statusFilter={investorStatusFilter}
          setStatusFilter={setInvestorStatusFilter}
          counts={investorStatusCounts}
          includeDeleted={true}
        />
      )}

      {activeTab === 'firms' && (
        <StatusFilterRow
          statusFilter={firmStatusFilter}
          setStatusFilter={setFirmStatusFilter}
          counts={firmStatusCounts}
          includeDeleted={false}
          includeHidden={true}
        />
      )}
      
      {activeTab === 'filters' ? (
        <FiltersManager />
      ) : (
      <div style={styles.tableWrapper} onScroll={handleTableScroll}>
        {loading || isLoading ? (
          <div style={styles.loading}>
            <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
            Loading data...
          </div>
        ) : activeTab === 'deleted' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.rowNumber }}>#</th>
                {deletedColumns.map(col => (
                  <th key={col.key} style={styles.th}>{col.label}</th>
                ))}
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData && filteredData.length > 0 ? (
                displayedData.map((row, index) => (
                  <tr key={row._id || index} style={{ background: '#fef2f2' }}>
                    <td style={{ ...styles.td, ...styles.rowNumber }}>{index + 1}</td>
                    {deletedColumns.map(col => (
                      <td key={col.key} style={styles.td}>
                        {col.key === 'deletedAt' && row[col.key]
                          ? new Date(row[col.key]).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : row[col.key] || <span style={{ color: '#9ca3af' }}>-</span>}
                      </td>
                    ))}
                    {renderDeletedActions(row)}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={deletedColumns.length + 2} style={{ ...styles.td, textAlign: 'center', padding: '48px' }}>
                    {searchQuery ? 'No matching results' : 'No deleted investors'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : activeTab === 'firms' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.rowNumber }}>#</th>
                {firmColumns.map(col => (
                  <th key={col.key} style={styles.th}>{col.label}</th>
                ))}
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredData && filteredData.length > 0 ? (
                displayedData.map((row, index) => (
                  <tr key={row._id || index} style={{ background: getRowBackground(row.status) }}>
                    <td style={{ ...styles.td, ...styles.rowNumber }}>{index + 1}</td>
                    {firmColumns.map(col => (
                      <EditableCell
                        key={col.key}
                        value={row[col.key]}
                        column={col}
                        rowId={row._id}
                        onSave={handleSaveFirm}
                      />
                    ))}
                    {renderFirmStatusCell(row)}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={firmColumns.length + 2} style={{ ...styles.td, textAlign: 'center', padding: '48px' }}>
                    {searchQuery ? 'No matching results' : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.rowNumber }}>#</th>
                {columns.map(col => (
                  <th key={col.key} style={styles.th}>{col.label}</th>
                ))}
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredData && filteredData.length > 0 ? (
                displayedData.map((row, index) => (
                  <tr key={row._id || index} style={{ background: getRowBackground(row.status) }}>
                    <td style={{ ...styles.td, ...styles.rowNumber }}>{index + 1}</td>
                    {columns.map(col => renderInvestorCell(row, col))}
                    {renderStatusCell(row)}
                    {renderDeleteCell(row)}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + 3} style={{ ...styles.td, textAlign: 'center', padding: '48px' }}>
                    {searchQuery ? 'No matching results' : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      )}

      <FirmPickerModal
        isOpen={firmPickerModal.open}
        onClose={() => setFirmPickerModal({ open: false, investorId: null, currentFirmId: null })}
        firms={firmsData}
        currentFirmId={firmPickerModal.currentFirmId}
        onSelect={handleFirmSelect}
      />

      <ConfirmModal
        isOpen={deleteModal.open}
        title="Delete Investor"
        message={`Are you sure you want to delete ${deleteModal.investor?.name || 'this investor'}? This investor will be moved to the Deleted repository.`}
        confirmLabel="Delete"
        confirmStyle={styles.modalDeleteBtn}
        onConfirm={() => {
          handleSoftDelete(deleteModal.investor);
          setDeleteModal({ open: false, investor: null });
        }}
        onCancel={() => setDeleteModal({ open: false, investor: null })}
      />

      <ConfirmModal
        isOpen={restoreModal.open}
        title="Restore Investor"
        message={`Are you sure you want to restore ${restoreModal.investor?.name || 'this investor'}? They will be moved back to the active investors list.`}
        confirmLabel="Restore"
        confirmStyle={styles.modalRestoreBtn}
        onConfirm={() => {
          handleRestore(restoreModal.investor);
          setRestoreModal({ open: false, investor: null });
        }}
        onCancel={() => setRestoreModal({ open: false, investor: null })}
      />

      <ConfirmModal
        isOpen={permanentDeleteModal.open}
        title="Permanently Delete"
        message={`Are you sure you want to permanently delete ${permanentDeleteModal.investor?.name || 'this investor'}? This action cannot be undone.`}
        confirmLabel="Permanently Delete"
        confirmStyle={styles.modalPermanentDeleteBtn}
        onConfirm={() => {
          handlePermanentDelete(permanentDeleteModal.investor);
          setPermanentDeleteModal({ open: false, investor: null });
        }}
        onCancel={() => setPermanentDeleteModal({ open: false, investor: null })}
      />
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DatabaseEditor;
